import { LitElement, html, css, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";

import "@material/web/textfield/outlined-text-field.js";
import "@material/web/button/filled-button.js";
import "@material/web/button/text-button.js";

import { pb } from "../lib/pb";
import { navigate } from "../lib/navigation";
import { inputValue, errorMessage } from "../lib/dom";
import { sharedStyles, authStyles } from "../styles/shared";

// One page, two halves of the same flow.
//
// Without a token in the URL it asks for an email and requests the reset; with
// one (the emailed link lands here as /reset-password?token=…) it asks for the
// new password and confirms it. The request half always claims success —
// whether the address exists is not something this endpoint may reveal, so the
// page says "if that address has an account, an email is on its way" either way.

@customElement("reset-password-page")
export class ResetPasswordPage extends LitElement {
  static styles = [
    sharedStyles,
    authStyles,
    css`
      .sent {
        background: var(--md-sys-color-secondary-container);
        color: var(--md-sys-color-on-secondary-container);
        padding: 12px 14px;
        border-radius: 12px;
        font-size: 0.9rem;
      }
      md-filled-button,
      md-text-button {
        width: 100%;
      }
    `,
  ];

  @state() private email = "";
  @state() private password = "";
  @state() private passwordConfirm = "";
  @state() private loading = false;
  @state() private error = "";
  @state() private requested = false;
  @state() private completed = false;

  private token = new URLSearchParams(window.location.search).get("token") ?? "";

  private async run(action: () => Promise<void>, fallback: string): Promise<void> {
    this.error = "";
    this.loading = true;
    try {
      await action();
    } catch (err) {
      this.error = errorMessage(err, fallback);
    } finally {
      this.loading = false;
    }
  }

  private handleRequest(e: Event): void {
    e.preventDefault();
    void this.run(async () => {
      await pb.collection("tenancy_users").requestPasswordReset(this.email);
      this.requested = true;
    }, "Could not request a reset. Try again in a moment.");
  }

  private handleConfirm(e: Event): void {
    e.preventDefault();
    void this.run(async () => {
      if (this.password !== this.passwordConfirm) {
        throw new Error("The passwords do not match.");
      }
      await pb
        .collection("tenancy_users")
        .confirmPasswordReset(this.token, this.password, this.passwordConfirm);
      this.completed = true;
    }, "Could not set the new password. The link may have expired — request a new one.");
  }

  private renderRequest() {
    if (this.requested) {
      return html`
        <p class="sent">
          If <strong>${this.email}</strong> has an account, an email with a
          reset link is on its way. The link expires after a short while.
        </p>
        <md-text-button href="/login">Back to sign in</md-text-button>
      `;
    }
    return html`
      <form @submit=${(e: Event) => this.handleRequest(e)}>
        <md-outlined-text-field
          label="Email"
          type="email"
          autocomplete="email"
          required
          .value=${this.email}
          @input=${(e: Event) => (this.email = inputValue(e))}
        ></md-outlined-text-field>
        ${this.error ? html`<p class="error-text">${this.error}</p>` : nothing}
        <md-filled-button type="submit" ?disabled=${this.loading}>
          ${this.loading ? "Sending…" : "Email me a reset link"}
        </md-filled-button>
        <md-text-button href="/login">Back to sign in</md-text-button>
      </form>
    `;
  }

  private renderConfirm() {
    if (this.completed) {
      return html`
        <p class="sent">Your password has been changed. Sign in with it now.</p>
        <md-filled-button @click=${() => void navigate("/login")}>
          Go to sign in
        </md-filled-button>
      `;
    }
    return html`
      <form @submit=${(e: Event) => this.handleConfirm(e)}>
        <md-outlined-text-field
          label="New password"
          type="password"
          autocomplete="new-password"
          required
          .value=${this.password}
          @input=${(e: Event) => (this.password = inputValue(e))}
        ></md-outlined-text-field>
        <md-outlined-text-field
          label="Repeat new password"
          type="password"
          autocomplete="new-password"
          required
          .value=${this.passwordConfirm}
          @input=${(e: Event) => (this.passwordConfirm = inputValue(e))}
        ></md-outlined-text-field>
        ${this.error ? html`<p class="error-text">${this.error}</p>` : nothing}
        <md-filled-button type="submit" ?disabled=${this.loading}>
          ${this.loading ? "Saving…" : "Set new password"}
        </md-filled-button>
      </form>
    `;
  }

  render() {
    const confirming = this.token !== "";
    return html`
      <div class="auth-page">
        <div class="auth-card">
          <h1>${confirming ? "Choose a new password" : "Reset your password"}</h1>
          <p class="subtitle">
            ${confirming
              ? "Pick a new password for your account."
              : "We will email you a link to choose a new one."}
          </p>
          ${confirming ? this.renderConfirm() : this.renderRequest()}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "reset-password-page": ResetPasswordPage;
  }
}
